import * as React from 'react';
import { Plug } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import type { EnabledConnectors } from '../types/chatTypes';
import { CHAT_CONNECTOR_DEFINITIONS } from '../config/connectors-config';

interface ConnectorsModalProps {
  isOpen: boolean;
  onClose: () => void;
  availableConnectors: EnabledConnectors;
  enabledConnectors: EnabledConnectors;
  onChange: (connectors: EnabledConnectors) => void;
  connectorDisplayNames?: Partial<Record<keyof EnabledConnectors, string>>;
}

export const ConnectorsModal: React.FC<ConnectorsModalProps> = ({
  isOpen,
  onClose,
  availableConnectors,
  enabledConnectors,
  onChange,
  connectorDisplayNames,
}) => {
  const visible = CHAT_CONNECTOR_DEFINITIONS.filter((c) => availableConnectors[c.id]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto z-[2100]">
        <DialogHeader className="space-y-3 text-left">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
              <Plug className="text-primary" size={18} />
            </div>
            <div className="min-w-0 space-y-1">
              <DialogTitle className="text-lg font-semibold text-slate-900 leading-tight">
                Connectors
              </DialogTitle>
              <DialogDescription className="text-xs text-slate-500 leading-relaxed">
                Turn on for this chat when you need them (enable each under Integrations first)
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-3 pt-1">
          {visible.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-6 px-2">
              No connectors are enabled for your account. Open{' '}
              <span className="font-medium text-slate-700">Integrations</span> in this add-in to allow EUIPO,
              EUR-Lex, CENDOJ, AEPD, OEPM, BOE, Tribunal Constitucional, or CNMC in chat.
            </p>
          ) : (
            visible.map((conn) => {
              const isEnabled = enabledConnectors[conn.id] ?? false;
              const title = connectorDisplayNames?.[conn.id] ?? conn.name;
              const id = `connector-${conn.id}`;
              return (
                <div
                  key={conn.id}
                  className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                    isEnabled ? 'bg-primary/5 border-primary/20' : 'bg-muted/30 border-border'
                  }`}
                >
                  <div className="w-11 h-11 rounded-lg bg-background border border-border flex items-center justify-center shrink-0 overflow-hidden">
                    <img
                      src={conn.logoUrl}
                      alt=""
                      className="w-7 h-7 object-contain"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <Label htmlFor={id} className="text-sm font-medium text-slate-900 cursor-pointer">
                      {title}
                    </Label>
                    <p className="text-xs text-slate-500 mt-0.5">{conn.description}</p>
                  </div>
                  <Checkbox
                    id={id}
                    checked={isEnabled}
                    onCheckedChange={(checked) =>
                      onChange({ ...enabledConnectors, [conn.id]: checked === true })
                    }
                    className="h-5 w-5"
                  />
                </div>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
